import { AlertTriangle, CheckCircle2, Flame, Snowflake } from "lucide-react";
import { useCallback, useEffect, useMemo } from "react";
import { useWatch } from "react-hook-form";
import { Alert, AlertDescription } from "../../../../components/ui/common/alert";
import { ToggleGroup, ToggleGroupItem } from "../../../../components/ui/common/toggle-group";
import { FormSelect } from "../../../../components/ui/Input/FormSelector/FormSelect";
import { FormUnitInput } from "../../../../components/ui/Input/FormUnitInput/FormUnitInput";
import {
  DISABLE_SEMI_SPECIFIC_COOLING,
  EXTENDED_STORAGE_SUFFIX
} from "../../../../config/constants";
import {
  CoolingMethodOption,
  ProcessDirectionOption
} from "../../../../config/enums/IngredientType.enum";
import {
  COOLING_METHOD_MIN_TEMPS,
  COOLING_METHOD_OPTIONS,
  DURATION_TYPE_OPTIONS
} from "../../../../config/optionsConstants";
import {
  validateCoolingMethodMinTemp,
  validateCoolingTemperatures,
  validateHeatingTemperatures,
  validatePercentage
} from "../../../../utils/utils";

const SemiSpecific = ({ control, setValue, existingProcess, index }: any) => {
  const getFieldName = useCallback(
    (field: string) => {
      return index !== undefined && index !== null ? `processes.${index}.${field}` : field;
    },
    [index]
  );

  // Common validation function for sub-components
  const validateSubComponentSum = (
    siblings: Array<{ value: any; name: string }>,
    parentValue: any,
    parentName: string
  ) => {
    const parent = Number(parentValue) || 0;

    const total = siblings.reduce((sum, sibling) => {
      return sum + (Number(sibling.value) || 0);
    }, 0);

    if (total <= parent) {
      return true;
    }

    const siblingDetails = siblings.map(s => `${s.name} (${Number(s.value) || 0})`).join(" + ");

    return `${siblingDetails} must not exceed ${parentName} (${parent})`;
  };

  // Watch all fields at once
  const [
    carbohydrates,
    protein,
    fat,
    water,
    ash,
    fiber,
    hydrocolloidsGums,
    starch,
    gellingProtein,
    gelatin,
    processDirection,
    coolingMethod,
    startingTemperature,
    targetTemperature,
    durationType,
    durationValue,
    processName
  ] = useWatch({
    control,
    name: [
      getFieldName("carbohydrates"),
      getFieldName("protein"),
      getFieldName("fat"),
      getFieldName("water"),
      getFieldName("ashMinerals"),
      getFieldName("fiber"),
      getFieldName("hydrocolloidsGums"),
      getFieldName("starch"),
      getFieldName("gellingProtein"),
      getFieldName("gelatin"),
      getFieldName("processDirection"),
      getFieldName("coolingMethod"),
      getFieldName("startingTemperature"),
      getFieldName("targetTemperature"),
      getFieldName("durationType"),
      getFieldName("durationValue"),
      getFieldName("processName")
    ]
  });
  const isCoolingMode =
    !DISABLE_SEMI_SPECIFIC_COOLING && processDirection === ProcessDirectionOption.COOLING;

  // Set default cooling method to IN_LINE_COOLING when entering cooling mode
  useEffect(() => {
    if (isCoolingMode && !coolingMethod) {
      setValue?.(getFieldName("coolingMethod"), String(CoolingMethodOption.IN_LINE_COOLING));
    }
  }, [isCoolingMode, coolingMethod, setValue, getFieldName]);

  // Set default unit to hours for extended cold storage when entering cooling mode
  useEffect(() => {
    if (isCoolingMode && !durationType) {
      setValue?.(getFieldName("durationType"), "HOURS");
    }
  }, [isCoolingMode, durationType, setValue, getFieldName]);

  // Flag the process as extended cold storage once a duration is entered, and keep the
  // "(CS)" suffix on the process name in sync with that flag. Only depends on durationValue
  // so typing in the process name doesn't retrigger this and fight the user's cursor.
  useEffect(() => {
    const isExtendedStorage =
      Number(durationValue) > 0 &&
      processDirection === ProcessDirectionOption.COOLING;

    setValue?.(getFieldName("extendedStorage"), isExtendedStorage);

    const baseName = (processName || "").replace(/\s*\(CS\)\s*$/, "");
    const nextName = isExtendedStorage ? `${baseName} ${EXTENDED_STORAGE_SUFFIX}`.trim() : baseName;

    if (nextName !== processName) {
      setValue?.(getFieldName("processName"), nextName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [durationValue, processDirection, setValue, getFieldName]);

  const handleProcessDirectionChange = (value: string) => {
    if (!value) return;
    setValue?.(getFieldName("processDirection"), value);
    if (value === ProcessDirectionOption.COOLING) {
      setValue?.(getFieldName("coolingMethod"), String(CoolingMethodOption.IN_LINE_COOLING));
    } else {
      setValue?.(getFieldName("coolingMethod"), null);
    }
  };

  const validateStartingTemperature = (value: any) => {
    if (DISABLE_SEMI_SPECIFIC_COOLING) return true;
    const starting = value !== "" && value !== null ? Number(value) : null;
    const target =
      targetTemperature !== "" && targetTemperature !== null ? Number(targetTemperature) : null;
    if (isCoolingMode) {
      return validateCoolingTemperatures(starting, target);
    }
    return true;
  };

  const validateTargetTemperature = (value: any) => {
    if (DISABLE_SEMI_SPECIFIC_COOLING) return true;
    const starting =
      startingTemperature !== "" && startingTemperature !== null
        ? Number(startingTemperature)
        : null;
    const target = value !== "" && value !== null ? Number(value) : null;
    if (isCoolingMode) {
      const coolingTempResult = validateCoolingTemperatures(starting, target);
      if (coolingTempResult !== true) return coolingTempResult;
      return validateCoolingMethodMinTemp(
        coolingMethod,
        target,
        COOLING_METHOD_MIN_TEMPS as Record<string, number>,
        COOLING_METHOD_OPTIONS
      );
    }
    return validateHeatingTemperatures(starting, target);
  };

  const macroTotal = useMemo(() => {
    const vals = [
      Number(water) || 0,
      Number(carbohydrates) || 0,
      Number(protein) || 0,
      Number(fat) || 0,
      Number(ash) || 0
    ];
    return vals.reduce<number>((sum, v) => sum + v, 0);
  }, [water, carbohydrates, protein, fat, ash]);

  const isValid = Math.abs(macroTotal - 100) < 0.001;

  return (
    <div className="tw-w-full tw-space-y-4">
      <div>
        <h4 className="tw-text-lg tw-font-semibold tw-text-foreground tw-font-display">
          Base Macronutrients
        </h4>
        <p className="tw-text-xs tw-text-muted-foreground tw-mt-0.5">
          Enter proportions as percentages
        </p>
      </div>
      <div className="tw-w-full tw-space-y-4 tw-rounded-md tw-border tw-border-border tw-p-3.5">
        <FormUnitInput
          control={control}
          name={getFieldName("carbohydrates")}
          label="Proportion of Carbohydrates"
          unitType={`%`}
          placeholder="0.00"
          required
          errorMessage="Proportion of Carbohydrates is required"
          disabled={existingProcess}
          enableLimitedDecimal
          validate={{
            valueCheck: (v: any) => validatePercentage(v) || "Value must be between 0 and 100"
          }}
        />
        <div className="tw-space-y-3 tw-pt-0">
          <p className="tw-text-xs tw-font-semibold tw-text-muted-foreground">
            Allocate the {(Number(carbohydrates) || 0).toFixed(2)} carbohydrates into the following
            components
          </p>
          <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-4">
            <FormUnitInput
              control={control}
              name={getFieldName("hydrocolloidsGums")}
              label="Hydrocolloids/Gums (Thick cold, thins hot)"
              placeholder="0.00"
              unitType={`%`}
              errorMessage="Hydrocolloids/Gums is required"
              disabled={existingProcess}
              enableLimitedDecimal
              validate={{
                valueCheck: (v: any) =>
                  validateSubComponentSum(
                    [
                      { value: v, name: "Hydrocolloids/Gums" },
                      { value: fiber, name: "Insoluble Fiber" },
                      { value: starch, name: "Starch" }
                    ],
                    carbohydrates,
                    "Carbohydrates"
                  )
              }}
            />
            <FormUnitInput
              control={control}
              name={getFieldName("fiber")}
              label="Insoluble Fiber (Constant friction)"
              placeholder="0.00"
              unitType={`%`}
              errorMessage="Insoluble Fiber is required"
              disabled={existingProcess}
              enableLimitedDecimal
              validate={{
                valueCheck: (v: any) =>
                  validateSubComponentSum(
                    [
                      { value: hydrocolloidsGums, name: "Hydrocolloids/Gums" },
                      { value: v, name: "Insoluble Fiber" },
                      { value: starch, name: "Starch" }
                    ],
                    carbohydrates,
                    "Carbohydrates"
                  )
              }}
            />
            <FormUnitInput
              control={control}
              name={getFieldName("starch")}
              label="Starch (Gels at 60°C)"
              placeholder="0.00"
              unitType={`%`}
              errorMessage="Starch is required"
              disabled={existingProcess}
              enableLimitedDecimal
              validate={{
                valueCheck: (v: any) =>
                  validateSubComponentSum(
                    [
                      { value: hydrocolloidsGums, name: "Hydrocolloids/Gums" },
                      { value: fiber, name: "Insoluble Fiber" },
                      { value: v, name: "Starch" }
                    ],
                    carbohydrates,
                    "Carbohydrates"
                  )
              }}
            />
          </div>
        </div>
      </div>

      <div className="tw-w-full tw-space-y-4 tw-rounded-md tw-border tw-border-border tw-p-3.5">
        <FormUnitInput
          control={control}
          name={getFieldName("protein")}
          label="Proportion of Protein"
          unitType={`%`}
          placeholder="0.00"
          required
          errorMessage="Proportion of Protein is required"
          disabled={existingProcess}
          enableLimitedDecimal
          validate={{
            valueCheck: (v: any) => validatePercentage(v) || "Value must be between 0 and 100"
          }}
        />
        <div className="tw-space-y-3 tw-pt-0">
          <p className="tw-text-xs tw-font-semibold tw-text-muted-foreground">
            Allocate the {(Number(protein) || 0).toFixed(2)} protein into the following components
          </p>
          <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-4">
            <FormUnitInput
              control={control}
              name={getFieldName("gellingProtein")}
              label="Heat-Gelling Protein (Gels at 75°C)"
              placeholder="0.00"
              unitType={`%`}
              errorMessage="Heat-Gelling Protein is required"
              disabled={existingProcess}
              enableLimitedDecimal
              validate={{
                valueCheck: (v: any) =>
                  validateSubComponentSum(
                    [
                      { value: v, name: "Heat-Gelling Protein" },
                      { value: gelatin, name: "Gelatin" }
                    ],
                    protein,
                    "Protein"
                  )
              }}
            />
            <FormUnitInput
              control={control}
              name={getFieldName("gelatin")}
              label="Gelatin (Solid cold, melts at 35°C)"
              placeholder="0.00"
              unitType={`%`}
              errorMessage="Gelatin is required"
              disabled={existingProcess}
              enableLimitedDecimal
              validate={{
                valueCheck: (v: any) =>
                  validateSubComponentSum(
                    [
                      { value: gellingProtein, name: "Heat-Gelling Protein" },
                      { value: v, name: "Gelatin" }
                    ],
                    protein,
                    "Protein"
                  )
              }}
            />
          </div>
        </div>
      </div>
      <div className="tw-w-full tw-space-y-4 tw-rounded-md tw-border tw-border-border tw-p-3.5">
        <FormUnitInput
          control={control}
          name={getFieldName("fat")}
          label="Proportion of Fat"
          unitType={`%`}
          placeholder="0.00"
          required
          errorMessage="Proportion of Fat is required"
          disabled={existingProcess}
          enableLimitedDecimal
          validate={{
            valueCheck: (v: any) => validatePercentage(v) || "Value must be between 0 and 100"
          }}
        />
        <div className="tw-space-y-3 tw-pt-0">
          <p className="tw-text-xs tw-font-semibold tw-text-muted-foreground">
            Allocate the {(Number(fat) || 0).toFixed(2)} fat into the following components
          </p>
          <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-1 tw-gap-4">
            <FormUnitInput
              control={control}
              name={getFieldName("solidFat")}
              label="Solid Fat (Melts at 35°C)"
              placeholder="0.00"
              unitType={`%`}
              disabled={existingProcess}
              enableLimitedDecimal
              validate={{
                valueCheck: (v: any) =>
                  validateSubComponentSum([{ value: v, name: "Solid Fat" }], fat, "Fat")
              }}
            />
          </div>
        </div>
      </div>
      <div className="tw-grid tw-w-full tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4">
        <FormUnitInput
          control={control}
          name={getFieldName("water")}
          label="Proportion of Water"
          unitType={`%`}
          placeholder="0.00"
          required
          errorMessage="Proportion of Water is required"
          disabled={existingProcess}
          enableLimitedDecimal
          validate={{
            valueCheck: (v: any) => validatePercentage(v) || "Value must be between 0 and 100"
          }}
        />
        <FormUnitInput
          control={control}
          name={getFieldName("ashMinerals")}
          label="Proportion Ash/Minerals"
          unitType={`%`}
          placeholder="0.00"
          required
          errorMessage="Proportion Ash/Minerals is required"
          disabled={existingProcess}
          enableLimitedDecimal
          validate={{
            valueCheck: (v: any) => validatePercentage(v) || "Value must be between 0 and 100"
          }}
        />
      </div>
      {macroTotal > 0 && (
        <Alert variant={isValid ? "default" : "destructive"} className="tw-p-3.5">
          <AlertDescription className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-p-0">
            {isValid ? (
              <>
                <CheckCircle2 className="tw-h-3.5 tw-w-3.5 tw-text-success" /> Total ={" "}
                {macroTotal.toFixed(3)} ✓
              </>
            ) : (
              <>
                <AlertTriangle className="tw-h-3.5 tw-w-3.5" /> Total = {macroTotal.toFixed(3)} —
                must equal 100
              </>
            )}
          </AlertDescription>
        </Alert>
      )}
      <div className="tw-space-y-4">
        <h4 className="tw-text-sm tw-font-semibold tw-text-foreground tw-font-display">
          Process Parameters
        </h4>

        <div className="tw-space-y-4">
          {!DISABLE_SEMI_SPECIFIC_COOLING && (
            <div className="tw-space-y-1.5">
              <label className="tw-text-sm tw-font-medium tw-text-foreground">
                Process Direction
                <span className="tw-text-destructive tw-ml-0.5">*</span>
              </label>
              <ToggleGroup
                type="single"
                value={processDirection || ProcessDirectionOption.HEATING}
                onValueChange={handleProcessDirectionChange}
                className="tw-justify-start tw-gap-2"
                disabled={existingProcess}
              >
                <ToggleGroupItem
                  value={ProcessDirectionOption.HEATING}
                  aria-label="Heating"
                  className="tw-gap-2 tw-px-4 tw-h-10 tw-border tw-border-border data-[state=on]:tw-bg-primary data-[state=on]:tw-text-primary-foreground data-[state=on]:tw-border-primary"
                >
                  <Flame className="tw-h-4 tw-w-4" />
                  Heating
                </ToggleGroupItem>
                <ToggleGroupItem
                  value={ProcessDirectionOption.COOLING}
                  aria-label="Cooling"
                  className="tw-gap-2 tw-px-4 tw-h-10 tw-border tw-border-border data-[state=on]:tw-bg-primary data-[state=on]:tw-text-primary-foreground data-[state=on]:tw-border-primary"
                >
                  <Snowflake className="tw-h-4 tw-w-4" />
                  Cooling
                </ToggleGroupItem>
              </ToggleGroup>
            </div>
          )}

          <div className={`tw-grid tw-gap-4 ${isCoolingMode ? "tw-grid-cols-1 sm:tw-grid-cols-4" : "tw-grid-cols-1 sm:tw-grid-cols-3"}`}>
            <FormUnitInput
              control={control}
              name={getFieldName("startingTemperature")}
              unitType="°C"
              label="Starting Temperature"
              placeholder="0"
              required
              errorMessage="Starting Temperature is required"
              disabled={existingProcess}
              enableLimitedDecimal
              enableDecimalWithMinus
              validate={{ temperatureRelationship: validateStartingTemperature }}
            />
            <FormUnitInput
              control={control}
              name={getFieldName("targetTemperature")}
              unitType="°C"
              label="Target Temperature"
              placeholder="0"
              required
              errorMessage="Target Temperature is required"
              disabled={existingProcess}
              enableDecimalWithMinus
              validate={{ temperatureRelationship: validateTargetTemperature }}
            />
            {!isCoolingMode ? (
              <FormUnitInput
                control={control}
                name={getFieldName("maintenanceTime")}
                label="Maintenance Time"
                unitType="minutes"
                placeholder="0"
                required
                errorMessage="Maintenance Time is required"
                disabled={existingProcess}
                enableLimitedDecimal
              />
            ) : (
              <>
                <FormSelect
                  control={control}
                  name={getFieldName("coolingMethod") as any}
                  label="Cooling Method"
                  placeholder="Select method"
                  required
                  errorMessage="Cooling Method is required"
                  options={COOLING_METHOD_OPTIONS}
                  disabled={existingProcess}
                />
                <FormUnitInput
                  control={control}
                  name={getFieldName("durationValue")}
                  label="Cold Storage (CS)"
                  unitName={getFieldName("durationType") as any}
                  unitOptions={DURATION_TYPE_OPTIONS}
                  placeholder="0"
                  disabled={existingProcess}
                  enableLimitedDecimal
                />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SemiSpecific;
